import { useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  setDoc
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

export function useFirestore<T extends { id: string }>(collectionName: string, initialData: T[] = []) {
  const [data, setData] = useState<T[]>(initialData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const q = query(collection(db, collectionName));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const items: any[] = [];
        snapshot.forEach((doc) => {
          items.push({ ...doc.data(), id: doc.id });
        });
        
        // Fix: If DB is empty, fall back to initialData (defaults) instead of showing nothing
        if (items.length === 0 && initialData.length > 0) {
           setData(initialData); 
        } else {
           setData(items as T[]);
        }
        setLoading(false);
      }, (error) => {
        console.error(`Error fetching ${collectionName}:`, error);
        // Fallback on error
        if (initialData.length > 0) setData(initialData);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error("Firebase init error:", err);
      if (initialData.length > 0) setData(initialData);
      setLoading(false);
    }
  }, [collectionName]);

  // CRUD Operations
  const add = async (item: Omit<T, 'id'> | T) => {
    try {
      if ('id' in item && item.id) {
          const { id, ...rest } = item as any;
          await setDoc(doc(db, collectionName, id), rest);
      } else {
          await addDoc(collection(db, collectionName), item);
      }
    } catch (e) {
      console.error("Error adding document:", e);
      throw e;
    }
  };

  const update = async (id: string, updates: Partial<T>) => {
    try {
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, updates as any);
    } catch (e) {
      console.error("Error updating document:", e);
      throw e;
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(db, collectionName, id));
    } catch (e) {
      console.error("Error deleting document:", e);
      throw e;
    }
  };

  return { data, loading, add, update, remove };
}